/**
 * openAiChatWithTaskUngated — fires one of the six AI task launchers.
 *
 * Resolves the host query tab per the launcher's `hostTabPolicy` (ruling F0.4,
 * SECOND AMENDMENT — see `launcherRegistry.ts`): `'reuse'` tasks reuse a tab
 * already bound to the connection, preferring the one the launcher fired from
 * via `ctx.tabId`; `'always-new'` tasks always open a fresh tab, skipping the
 * lookup, because they have no originating tab and the first-match fallback
 * can otherwise hijack a stranger's unsaved buffer. Opens the resolved tab's
 * AI chat sidebar, and parks the built prompt in
 * `aiTaskLaunchStore`. The tab-side drain hook (`useAiTaskLaunchDrain`) sends it
 * as soon as the tab is mounted and idle; there is no composer prefill, because
 * the composer's text is local state and the message belongs in the transcript
 * anyway (ruling F0.3).
 *
 * **Not the call-site API — use `useAiTaskLauncher().launchTask`.** The name
 * carries "Ungated" because this function opens the sidebar directly, which is
 * exactly the bypass ruling F0.3 names: it cannot check whether Claude Code or
 * Codex is installed (that is a hook), so on its own it would hand a user with
 * no CLI a sidebar that can never answer. `useAiTaskLauncher` wraps it in
 * `useCliDetection().checkAndOpenSidebar`, and this symbol is deliberately kept
 * out of the feature's `index.ts` so no menu entry reaches it by accident.
 */

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import type { TabConnectionInfo } from '../../workspace/core/types/workspaceTypes';
import { useAiChatStore } from '../store/aiChatStore';
import { useAiTaskLaunchStore } from '../store/aiTaskLaunchStore';
import { findExistingQueryTab } from './findExistingQueryTab';
import { launchers } from './launcherRegistry';
import type { AiTaskContext, AiTaskId, AiTaskSurface } from './launcherRegistry';

/**
 * A new tab needs more than the connection id to render a useful chat context
 * (server host and connection name reach the agent's system prompt), so borrow
 * them from any tab already on that connection. The caller's database always
 * wins — it is the one the task is actually about.
 */
function connectionInfoFor(
  tabs: ReturnType<typeof useWorkspaceStore.getState>['tabs'],
  connectionId: string,
  database?: string,
): TabConnectionInfo {
  const donor = tabs.find((tab) => tab.connectionInfo?.connectionId === connectionId)
    ?.connectionInfo;
  return {
    ...donor,
    connectionId,
    ...(database ? { database } : {}),
  };
}

export async function openAiChatWithTaskUngated(
  id: AiTaskId,
  ctx: AiTaskContext,
  surface: AiTaskSurface,
): Promise<void> {
  const { tabs, activeTabId, setActiveTab, addTab, getNextQueryNumber } =
    useWorkspaceStore.getState();

  // F0.4 SECOND AMENDMENT (fix-round-T13-r1.md, C-1): host-tab reuse is a
  // property of the task, not a decision this function makes for every
  // caller. `hostTabPolicy: 'always-new'` tasks (generate-test-data,
  // diagnose-server-load) skip the lookup entirely — they have no originating
  // query tab, and the first-match fallback below can otherwise land on a
  // stranger's unsaved buffer and full-replace it via ui_set_editor_text.
  const existing =
    launchers[id].hostTabPolicy === 'always-new'
      ? undefined
      : // Preference order: the tab the launcher fired from, then the tab the
        // user is looking at. Both are ignored unless they are candidates in
        // their own right.
        findExistingQueryTab(tabs, ctx.connectionId, ctx.database, [
          ctx.tabId,
          activeTabId,
        ]);
  let tabId: string;
  if (existing) {
    tabId = existing.id;
    setActiveTab(tabId);
  } else {
    tabId = addTab({
      type: 'query',
      title: `Query-${getNextQueryNumber()}`,
      connectionInfo: connectionInfoFor(tabs, ctx.connectionId, ctx.database),
    });
  }

  // Entries can only be drained by a mounted tab, so anything left behind by a
  // closed one is dead weight — clear it while we are here.
  const launchStore = useAiTaskLaunchStore.getState();
  launchStore.pruneQueues([
    ...useWorkspaceStore.getState().tabs.map((tab) => tab.id),
  ]);

  // `silent`: this launch is already counted by `feature:ai_task_launcher`
  // below. Letting `setSidebarOpen` fire `feature:ai_chatbot` too would count
  // one launch twice, in an `adapter: 'unknown'` bucket for freshly created tabs.
  useAiChatStore.getState().setSidebarOpen(tabId, true, { silent: true });
  // `surface` is not a prompt input — it is the analytics dimension below.
  // (It used to be threaded into `buildPrompt` for `draft-migration`'s two
  // differently-worded buttons; that task is gone, and so is the parameter.)
  launchStore.enqueue(tabId, { taskId: id, prompt: launchers[id].buildPrompt(ctx) });

  if (typeof window !== 'undefined') {
    window.electronAPI?.analytics
      ?.track('feature:ai_task_launcher', { task: id, surface })
      .catch(() => {
        /* fire-and-forget */
      });
  }
}
